import { supabase } from "@/integrations/supabase/client";
import { getPlatform, isNativePlatform } from "@/lib/platform";
import { hasAnalyticsConsent } from "@/lib/consent";

type EventType =
  | "pricing_viewed"
  | "plan_viewed"
  | "plan_clicked"
  | "checkout_initiated"
  | "checkout_abandoned"
  | "checkout_failed"
  | "checkout_succeeded";

export interface PaymentEventPayload {
  plan_id?: string;
  plan_name?: string;
  price_amount?: number;
  currency?: string;
  from_tier?: string;
  to_tier?: string;
  provider?: "dodo" | "apple_iap" | string;
  region?: string;
  country_code?: string;
  metadata?: Record<string, unknown>;
}

const SESSION_KEY = "pa_session_id";

function getSessionId(): string {
  try {
    let s = sessionStorage.getItem(SESSION_KEY);
    if (!s) {
      s = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_KEY, s);
    }
    return s;
  } catch {
    return `${Date.now().toString(36)}`;
  }
}

/**
 * Fire-and-forget payment funnel event log.
 * Sends to `log-payment-event` edge function.
 * No-op on web when analytics consent is not granted; always on for native apps.
 */
export function logPaymentEvent(event_type: EventType, payload: PaymentEventPayload = {}) {
  try {
    const native = isNativePlatform();
    if (!native && !hasAnalyticsConsent()) return;

    const platform = getPlatform(); // web | ios | android

    // Best-effort attach user_id if we already have a session
    let user_id: string | undefined;
    supabase.auth.getSession().then(({ data }) => {
      user_id = data.session?.user?.id;
      void send({ event_type, platform, user_id, payload });
    }).catch(() => {
      void send({ event_type, platform, payload });
    });
  } catch (e) {
    console.warn("logPaymentEvent failed:", e);
  }
}

async function send(args: {
  event_type: EventType;
  platform: string;
  user_id?: string;
  payload: PaymentEventPayload;
}) {
  const { event_type, platform, user_id, payload } = args;
  const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
  if (!projectId) return;

  const body = {
    event_type,
    platform,
    user_id,
    session_id: getSessionId(),
    page_path: typeof location !== "undefined" ? location.pathname + location.search : null,
    referrer: typeof document !== "undefined" ? document.referrer || null : null,
    ...payload,
  };

  try {
    // Prefer supabase.functions.invoke to auto-attach the anon key & auth
    await supabase.functions.invoke("log-payment-event", { body });
  } catch (e) {
    // fallback: direct fetch (fire-and-forget)
    try {
      await fetch(
        `https://${projectId}.supabase.co/functions/v1/log-payment-event`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );
    } catch { /* swallow */ }
  }
}
